import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Box } from "@mui-wrapper";
import { CARDS_IN_DOM_UP_AND_DOWN, DEFAULT_COLUMN_CONFIG, ESTIMATE_HEIGHT_CARD, getColumnCountFromConfig, getColumnCountFromWidth, GRID_COLUMN_GAP } from "@components/virtualized/virtualized-grid-utils";
import { useDynamicHeight } from "@hooks/use-dynamic-height";
import { useMediaQueryList } from "@hooks/use-media-query";
/**
 * A responsive virtualized grid component optimized for performance with large lists.
 *
 * @template T The type of each item in the `items` array
 */
export function VirtualizedGrid({
  items,
  isLoading,
  loadMoreItems,
  renderItem,
  shouldSkipDynamicHeight = false,
  renderSkeletonItem,
  estimateSize = ESTIMATE_HEIGHT_CARD,
  overscan = CARDS_IN_DOM_UP_AND_DOWN,
  columnConfig = DEFAULT_COLUMN_CONFIG,
  minColumnWidth,
  shouldMeasureRowHeight = false
}) {
  const {
    ref: dynamicHeightRef,
    height: dynamicHeight
  } = useDynamicHeight({
    shouldSkip: shouldSkipDynamicHeight,
    deps: [items.length, isLoading]
  });
  const containerRef = useRef(null);
  const rowsRef = useRef(null);
  const isFetchingRef = useRef(false);
  const gridWidth = useGridWidth(rowsRef, minColumnWidth !== undefined);
  const {
    isScreenXlOrLarger,
    isScreenLgOrLarger,
    isScreenMdOrLarger,
    isScreenSmOrLarger,
    isScreenXXL,
    isScreenXXXL
  } = useMediaQueryList();
  const breakpointColumnCount = getColumnCountFromConfig(columnConfig, {
    isScreenXXXL,
    isScreenXXL,
    isXL: isScreenXlOrLarger,
    isLG: isScreenLgOrLarger,
    isMD: isScreenMdOrLarger && !isScreenLgOrLarger,
    isSM: isScreenSmOrLarger && !isScreenMdOrLarger
  });
  const columnCount = minColumnWidth !== undefined && gridWidth > 0 ? getColumnCountFromWidth({
    gridWidth,
    minColumnWidth,
    maxColumnCount: breakpointColumnCount
  }) : breakpointColumnCount;

  /**
   * Default row sx: grid layout with dynamic columns.
   */
  const defaultRowSx = {
    display: "grid",
    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
    gap: columnCount > 1 ? `${GRID_COLUMN_GAP}px` : 0,
    position: "absolute",
    width: "100%",
    // Fixed row height for predictable virtualization and performance.
    // Skipped when dynamic measurement is enabled so rows can size to content.
    height: shouldMeasureRowHeight ? undefined : estimateSize,
    // Using translate3d triggers GPU acceleration for smoother scrolling compared to translateY
    transform: "translate3d(0, var(--virtualization-row), 0)",
    willChange: "transform"
  };

  /**
   * Returns the list rendered by the VirtualGrid
   * When loading, adds `columnCount * 2` null items so the grid shows two
   * full rows of skeletons (passed via props) and the layout doesn't jump
   */
  const displayItems = useMemo(() => {
    return isLoading ? [...items, ...Array(columnCount * 2).fill(null)] : items;
  }, [items, isLoading, columnCount]);
  const rowCount = Math.ceil(displayItems.length / columnCount);

  /** Virtualizer instance for row virtualization */
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => containerRef.current,
    estimateSize: () => estimateSize,
    overscan,
    measureElement: shouldMeasureRowHeight ? element =>
    // A hidden ancestor (e.g. `display: none` while the map is full-screen)
    // collapses the row to 0; fall back to the estimate so that 0 is never
    // cached, which would otherwise break the layout once the list reappears.
    element.getBoundingClientRect().height || estimateSize : undefined
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  /**
   * Triggers `loadMoreItems` when the user scrolls near the end of the list.
   * Prevents duplicate calls using `isFetchingRef`.
   */
  useEffect(() => {
    if (!loadMoreItems || isLoading) {
      return;
    }
    const lastVirtual = virtualItems.at(-1);
    if (!lastVirtual) {
      // No virtualized items available
      return;
    }
    const lastRealRowIndex = Math.ceil(items.length / columnCount) - 1;
    const shouldTrigger = lastVirtual.index >= lastRealRowIndex && !isLoading && !isFetchingRef.current;
    if (!shouldTrigger) {
      return;
    }
    isFetchingRef.current = true;
    Promise.resolve(loadMoreItems()).catch(() => {
      // Ignore errors — this component only triggers load
    }).finally(() => {
      isFetchingRef.current = false;
    });
    return () => {
      // Cleanup: reset fetching flag if component unmounts during load
      isFetchingRef.current = false;
    };
  }, [items.length, columnCount, isLoading, loadMoreItems, virtualItems]);

  /**
   * Render a single cell (item or skeleton)
   */
  function renderCell(item, index) {
    return item ? renderItem(item, index) : renderSkeletonItem(index);
  }
  return <Box ref={dynamicHeightRef} sx={{
    height: shouldSkipDynamicHeight ? undefined : dynamicHeight,
    display: "flex",
    flexDirection: "column",
    position: "relative"
  }}>
      <Box ref={containerRef} sx={{
      position: "relative",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      overflow: "auto",
      contain: "strict",
      p: "1px",
      minHeight: 0
    }}>
        <Box ref={rowsRef} sx={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: "relative",
        width: "100%",
        flex: "0 0 auto"
      }}>
          {virtualItems.map(virtualRow => {
          const rowIndex = virtualRow.index;
          const startIndex = rowIndex * columnCount;
          return <Box key={virtualRow.key} data-index={virtualRow.index} ref={shouldMeasureRowHeight ? rowVirtualizer.measureElement : undefined} sx={defaultRowSx} style={{
            // CSS variable for position to avoid recalculating sx on every render
            // eslint-disable-next-line @typescript-eslint/naming-convention
            "--virtualization-row": `${virtualRow.start}px`
          }}>
                {Array.from({
              length: columnCount
            }).map((_, colIndex) => {
              const itemIndex = startIndex + colIndex;
              const item_0 = displayItems[itemIndex];
              if (itemIndex >= displayItems.length) {
                return null;
              }
              return renderCell(item_0, itemIndex);
            })}
              </Box>;
        })}
        </Box>
      </Box>
    </Box>;
}

/**
 * Returns the width of the element and keeps it current as it resizes, or 0 while not measured.
 * Measured before paint, so a grid sized by it does not first show the breakpoint's maximum.
 */
function useGridWidth(ref, shouldMeasure) {
  const $ = _c(4);
  const [width, setWidth] = useState(0);
  let t0;
  let t1;
  if ($[0] !== ref || $[1] !== shouldMeasure) {
    t0 = () => {
      const element = ref.current;
      if (!shouldMeasure || !element) {
        return;
      }
      setWidth(element.clientWidth);
      const observer = new ResizeObserver(t2 => {
        const [entry] = t2;
        setWidth(entry.contentRect.width);
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t1 = [ref, shouldMeasure];
    $[0] = ref;
    $[1] = shouldMeasure;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
  return width;
}